/**
 * React Query Configuration with IndexedDB persistence
 *
 * Configures React Query with optimal settings for offline-first applications,
 * including IndexedDB persistence and background refetching.
 *
 * References:
 * - @tanstack/react-query - Query library
 * - @tanstack/react-query-persist-client - Persistence plugin
 * - src/services/indexedDBService.js - Storage backend
 */

import React from 'react';
import PropTypes from 'prop-types';
import { QueryClient, QueryClientProvider as ReactQueryProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import logger from '../utils/logger';
import indexedDBService from '../services/indexedDBService';

// Create custom IndexedDB persister
const createIndexedDBPersister = () => ({
  persistClient: async (client) => {
    if (!indexedDBService.isSupported) return;

    try {
      const dehydratedState = client.getQueryCache().getAll().reduce((acc, query) => {
        if (query.state.status === 'success' && query.state.data) {
          acc[query.queryKey.join(':')] = {
            data: query.state.data,
            timestamp: Date.now(),
          };
        }
        return acc;
      }, {});

      await indexedDBService.set('queryCache', 'react-query', dehydratedState);
      logger.debug('[ReactQueryConfig] Persisted query cache to IndexedDB');
    } catch (error) {
      logger.error('[ReactQueryConfig] Failed to persist query cache:', error);
    }
  },

  restoreClient: async () => {
    if (!indexedDBService.isSupported) return {};

    try {
      const cached = await indexedDBService.get('queryCache', 'react-query');
      if (cached) {
        logger.debug('[ReactQueryConfig] Restored query cache from IndexedDB');
        return cached;
      }
    } catch (error) {
      logger.warn('[ReactQueryConfig] Failed to restore query cache:', error);
      // Panic reset: clear persisted cache so app can boot (dev or stale SW/cache issues)
      try {
        await indexedDBService.delete('queryCache', 'react-query');
        logger.warn('[ReactQueryConfig] Cleared persisted query cache after restore failure');
      } catch (clearErr) {
        logger.warn('[ReactQueryConfig] Could not clear query cache:', clearErr);
      }
    }
    return {};
  },

  removeClient: async () => {
    if (!indexedDBService.isSupported) return;

    try {
      await indexedDBService.delete('queryCache', 'react-query');
      logger.debug('[ReactQueryConfig] Removed persisted query cache');
    } catch (error) {
      logger.warn('[ReactQueryConfig] Failed to remove query cache:', error);
    }
  },
});

// Create the query client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - unused data kept in cache
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times with exponential backoff
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false, // Don't refetch on window focus (saves bandwidth)
      refetchOnMount: false, // Don't refetch on mount if data is fresh
      networkMode: 'offlineFirst', // Prioritize cached data when offline
    },
    mutations: {
      retry: false, // Don't retry mutations (let offline queue handle it)
      networkMode: 'offlineFirst',
    },
  },
});

// Set up persistence (skip in demo mode)
if (indexedDBService.isSupported) {
  const persister = createIndexedDBPersister();

  persistQueryClient({
    queryClient,
    persister,
    maxAge: 24 * 60 * 60 * 1000, // 24 hours
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        // Only persist successful queries with data
        return query.state.status === 'success' && !!query.state.data;
      },
    },
  });

  logger.info('[ReactQueryConfig] Query persistence enabled with IndexedDB');
} else {
  logger.info('[ReactQueryConfig] Query persistence disabled (demo mode or IndexedDB not supported)');
}

// QueryClientProvider wrapper component
const QueryClientProviderWrapper = ({ children }) => {
  return (
    <ReactQueryProvider client={queryClient}>
      {children}
    </ReactQueryProvider>
  );
};

QueryClientProviderWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

export { QueryClientProviderWrapper as QueryClientProvider };
export default queryClient;
